/**
 * Mi trabajo (BHM-CLD-REQ-028): tareas del empleado en todos sus proyectos.
 * Orden: bloqueadas → vencidas → alta prioridad → próximas a vencer → resto.
 * Encima: flujo de la jornada ANTERIOR · EN EJECUCIÓN · SIGUIENTE (REQ-045), «Finalizar jornada»
 * y la acción «Tarea ya realizada» (045b).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import TaskVisualHierarchy from '../components/tasks/TaskVisualHierarchy';

import { Link, useNavigate } from 'react-router-dom';
import { useNexusAccess, nexusTaskUrl } from '../components/work/useNexusAccess';
import { useAuthStore } from '../stores/authStore';
import { usePermissionStore } from '../stores/permissionStore';
import { isSuperAdmin } from '../utils/permissions';
import {
  workApi,
  fmtDate,
  PRIORITY_LABELS,
  PROJECT_TYPE_OPTIONS,
  TASK_STATUS_LABELS,
  type TaskRow,
  type TaskStatus,
} from '../stores/workApi';
import { Icon, Pill, PriorityLabel, StatusPill, ErrorBanner, TimerBar, useToast, inputCls } from '../components/work/WorkUi';
import { TaskStatusSelect } from './ProyectoDetallePage';
import { errText } from '../stores/activityApi';
import { ProcessChip, ProjectTypeBadge } from '../components/work/ProcessPicker';

import MyWorkFlow from '../components/work/MyWorkFlow';
import AlreadyDoneDialog from '../components/work/AlreadyDoneDialog';
import { FinishDayButton } from '../components/work/FinishDay';

const BUCKETS: Record<number, { label: string; cls: string; icon: string }> = {
  1: { label: 'Bloqueadas', cls: 'text-[#F43F5E]', icon: 'block' },
  2: { label: 'Vencidas', cls: 'text-[#F43F5E]', icon: 'event_busy' },
  3: { label: 'Alta prioridad', cls: 'text-[#F59E0B]', icon: 'priority_high' },
  4: { label: 'Próximas a vencer', cls: 'text-[#60a5fa]', icon: 'upcoming' },
  5: { label: 'Resto', cls: 'text-on-surface-variant', icon: 'list' },
};

type Range = 'all' | 'today' | 'week';

export default function MiTrabajoPage() {
  const nexusOk = useNexusAccess();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const perms = usePermissionStore((s) => s.permissions);
  const sa = isSuperAdmin(user?.role ?? '');
  const canTeam = sa || perms.includes('TASKS_ADMIN');
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const [range, setRange] = useState<Range>('all');
  const [projectId, setProjectId] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [scope, setScope] = useState<'me' | 'team'>('me');
  const [processF, setProcessF] = useState('');
  const [typeF, setTypeF] = useState('');
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  useEffect(() => { const h = setTimeout(() => setQApplied(q.trim()), 300); return () => clearTimeout(h); }, [q]);
  const [error, setError] = useState<string | null>(null);
  const [timerKey, setTimerKey] = useState(0);
  const [flowKey, setFlowKey] = useState(0);
  const [doneTarget, setDoneTarget] = useState<TaskRow | null>(null);
  const { notify, node } = useToast();

  const load = useCallback(async () => {
    try {
      setTasks(await workApi.myTasks({ range, projectId: projectId || undefined, priority: priority || undefined, status, scope, q: qApplied || undefined, process: processF || undefined, ...(typeF.startsWith('INTERNAL:') ? { internalCategory: typeF.split(':')[1] } : typeF ? { projectType: typeF } : {}) }));
    } catch (e) { setError(errText(e, 'No se pudieron cargar tus tareas')); setTasks([]); }
  }, [range, projectId, priority, status, scope, qApplied, processF, typeF]);
  useEffect(() => { void load(); }, [load]);

  const projects = useMemo(() => {
    const m = new Map<string, string>();
    (tasks ?? []).forEach((t) => m.set(t.project.id, t.project.name));
    return [...m.entries()];
  }, [tasks]);
  const grouped = useMemo(() => {
    const g = new Map<number, TaskRow[]>();
    (tasks ?? []).forEach((t) => g.set(t.bucket, [...(g.get(t.bucket) ?? []), t]));
    return [...g.entries()].sort((a, b) => a[0] - b[0]);
  }, [tasks]);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    try { await fn(); if (ok) notify(ok); await load(); } catch (e) { setError(errText(e)); }
  };
  /** Tras iniciar tiempo desde «Siguiente»: tareas, flujo y barra del temporizador. */
  const afterFlowStart = async () => { await load(); setFlowKey((k) => k + 1); setTimerKey((k) => k + 1); };
  /** Tras finalizar la jornada: tareas y barra del temporizador (contador y flujo ya avisados). */
  const afterFinishDay = async () => { await load(); setTimerKey((k) => k + 1); };

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface">Mi trabajo</h1>
          <p className="mt-1 text-body-md text-on-surface-variant">{scope === 'team' ? 'Tareas abiertas del equipo' : 'Tus tareas en todos tus proyectos'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <FinishDayButton onChanged={afterFinishDay} />
          {canTeam && (
            <div className="flex gap-1 rounded-lg border border-outline-variant p-1" role="tablist">
              {(['me', 'team'] as const).map((s) => (
                <button key={s} role="tab" aria-selected={scope === s} onClick={() => setScope(s)}
                  className={`rounded-md px-3 py-1.5 text-sm ${scope === s ? 'bg-[#FF5E1E]/15 font-medium text-[#FF5E1E]' : 'text-on-surface-variant'}`}>
                  {s === 'me' ? 'Mis tareas' : 'Equipo'}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <MyWorkFlow refreshKey={flowKey} onStarted={afterFlowStart} />

      <TimerBar refreshKey={timerKey} onStopped={() => notify('Tiempo guardado como borrador. Revísalo y envíalo en Tiempo.')} />

      <div className="flex flex-wrap items-center gap-2">
        {([['all', 'Todas'], ['today', 'Hoy'], ['week', 'Esta semana']] as [Range, string][]).map(([k, v]) => (
          <button key={k} onClick={() => setRange(k)} className={`rounded-full border px-3 py-1 text-xs font-medium ${range === k ? 'border-[#D946EF]/50 bg-[#D946EF]/15 text-[#D946EF]' : 'border-outline-variant text-on-surface-variant'}`}>{v}</button>
        ))}
        <input className={`${inputCls} w-44`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar BM-0123 o texto" aria-label="Buscar tarea por código o título" data-testid="task-search" />
        <select className={`${inputCls} w-52`} value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Proyecto">
          <option value="">Todos los proyectos</option>
          {projects.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select className={`${inputCls} w-40`} value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Prioridad">
          <option value="">Cualquier prioridad</option>
          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className={`${inputCls} w-52`} value={typeF} onChange={(e) => setTypeF(e.target.value)} aria-label="Tipo de proyecto" data-testid="filter-project-type">
          <option value="">Todos los tipos</option>
          <option value="EXTERNAL">Externo</option>
          <option value="INTERNAL">Interno (todos)</option>
          {PROJECT_TYPE_OPTIONS.filter((o) => o.internalCategory).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          <option value="PENDING_CLASSIFICATION">Proyecto pendiente de clasificar</option>
        </select>
        <select className={`${inputCls} w-52`} value={processF} onChange={(e) => setProcessF(e.target.value)} aria-label="Proceso" data-testid="filter-process">
          <option value="">Cualquier proceso</option>
          <option value="PENDING">Pendiente de clasificar</option>
        </select>
        <select className={`${inputCls} w-40`} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
          <option value="OPEN">Abiertas</option>
          {(['TODO', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW', 'DONE'] as TaskStatus[]).map((s) => <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      <ErrorBanner message={error} onClose={() => setError(null)} />

      {tasks === null && <p className="py-10 text-center text-sm text-on-surface-variant">Cargando…</p>}
      {tasks !== null && tasks.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-outline-variant py-16 text-center">
          <Icon name="assignment" className="text-5xl text-outline" />
          <p className="text-sm text-on-surface-variant">No tienes tareas con estos filtros.</p>
          <p className="text-xs text-outline">Las tareas aparecen aquí cuando te asignan a un proyecto y a sus tareas.</p>
        </div>
      )}
      {grouped.map(([bucket, list]) => (
        <section key={bucket} className="space-y-2">
          <h2 className={`flex items-center gap-2 text-sm font-semibold ${BUCKETS[bucket].cls}`}><Icon name={BUCKETS[bucket].icon} className="text-base" />{BUCKETS[bucket].label} · {list.length}</h2>
          <ul className="space-y-2">
            {list.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-outline-variant bg-surface-container-high/40 px-4 py-3" data-testid="my-task">
                <div className="min-w-0 flex-1">
                  <div className="min-w-0"><TaskVisualHierarchy task={{ projectName: t.project?.name ?? null, processName: t.process?.name ?? null, taskCode: t.taskCode, title: t.title, assigneeName: t.assignee?.name ?? null, location: (t as { location?: string | null }).location ?? null, description: (t as { description?: string | null }).description ?? null }} variant="compact" /></div>
                  <p className="flex flex-wrap items-center gap-1.5 pb-0.5"><ProjectTypeBadge label={t.project.typeLabel} type={t.project.projectType} /><ProcessChip process={t.process} /></p>
                  <p className="truncate text-xs text-on-surface-variant">
                    <Link to={`/proyectos/${t.project.id}`} className="hover:text-on-surface">{t.project.name}</Link>
                    {' · '}<Link to={`/proyectos/${t.project.id}?tab=tareas&task=${t.id}`} className="hover:text-on-surface" data-testid="my-task-open">Abrir en el proyecto</Link>
                    {t.project.clientName ? ` · ${t.project.clientName}` : ''}{scope === 'team' && t.assignee ? ` · ${t.assignee.name}` : ''}
                  </p>
                </div>
                <PriorityLabel priority={t.priority} />
                <span className="text-xs text-on-surface-variant">{t.dueDate ? `Vence ${fmtDate(t.dueDate)}` : 'Sin fecha'}</span>
                {t.capabilities.changeStatus && (
                  <span className="flex gap-1" aria-label="Acciones rápidas">
                    {t.status === 'TODO' && <button className="rounded-lg border border-[#3b82f6]/40 px-2 py-1 text-xs text-[#60a5fa] hover:bg-[#3b82f6]/10" onClick={() => act(() => workApi.updateTask(t.id, { status: 'IN_PROGRESS' }), 'Tarea en curso')}>Empezar</button>}
                    {!['BLOCKED', 'DONE'].includes(t.status) && <button className="rounded-lg border border-[#F43F5E]/40 px-2 py-1 text-xs text-[#F43F5E] hover:bg-[#F43F5E]/10" onClick={() => act(() => workApi.updateTask(t.id, { status: 'BLOCKED' }), 'Tarea bloqueada')}>Bloquear</button>}
                    {t.status !== 'DONE' && <button className="rounded-lg border border-[#10B981]/40 px-2 py-1 text-xs text-[#10B981] hover:bg-[#10B981]/10" onClick={() => act(() => workApi.updateTask(t.id, { status: 'DONE' }), 'Tarea completada')}>Completar</button>}
                    {t.status !== 'DONE' && <button className="rounded-lg border border-outline px-2 py-1 text-xs text-on-surface-variant hover:bg-surface-container-highest" data-testid="already-done" title="La tarea procedía pero ya se ejecutó" onClick={() => setDoneTarget(t)}>Tarea ya realizada</button>}
                  </span>
                )}
                {t.capabilities.changeStatus ? <TaskStatusSelect task={t} onChange={(s) => act(() => workApi.updateTask(t.id, { status: s }), 'Estado actualizado')} /> : <StatusPill status={t.status} />}
                {nexusOk && (
                  <button className="inline-flex items-center gap-1 rounded-lg bg-[#FF5E1E] px-2.5 py-1.5 text-xs font-medium text-white hover:bg-[#FF5E1E]/90" data-testid="resolve-with-nexus" onClick={() => navigate(nexusTaskUrl(t.project.id, t.id))}>
                    <Icon name="bolt" className="text-sm" />Resolver con Nexus
                  </button>
                )}
                {t.capabilities.logTime && (
                  <button className="inline-flex items-center gap-1 rounded-lg border border-[#FF5E1E]/40 px-2.5 py-1.5 text-xs font-medium text-[#FF5E1E] hover:bg-[#FF5E1E]/10"
                    onClick={() => act(async () => { await workApi.startTimer(t.project.id, t.id); setTimerKey((k) => k + 1); }, 'Temporizador iniciado')}>
                    <Icon name="play_arrow" className="text-sm" />Iniciar tiempo
                  </button>
                )}
                {t.source !== 'BARMETHUB' && <Pill className="border-outline-variant text-outline">{t.source === 'HOLDED' ? 'Holded' : 'Asana'}</Pill>}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {doneTarget && (
        <AlreadyDoneDialog task={{ id: doneTarget.id, title: doneTarget.title, projectName: doneTarget.project.name }} source="MY_TASKS"
          onClose={() => setDoneTarget(null)} onDone={async (msg) => { notify(msg); await load(); }} />
      )}
      {node}
    </div>
  );
}
